import React, { useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowRight,
  CheckCircle2,
  UploadCloud,
  Sparkles,
  BookOpen,
  Briefcase,
  GraduationCap,
  Users,
  Compass,
  Activity,
  Car,
  Monitor,
  Smartphone,
  MessageSquare,
  ShieldCheck,
  Headphones
} from 'lucide-react';
import { WaitlistForm } from './WaitlistForm';

export function LandingPage() {
  const { isLoggingIn, openAuth } = useAuth();
  const waitlistRef = useRef<HTMLDivElement>(null);

  const scrollToWaitlist = () => {
    waitlistRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const audienceCards = [
    {
      icon: Briefcase,
      title: 'Organisations and training teams',
      body: 'Turn policies, SOPs, onboarding packs, internal documents, and training materials into reusable learning and source grounded support.'
    },
    {
      icon: GraduationCap,
      title: 'Tutors, trainers, and educators',
      body: 'Create structured learning from your existing notes, slides, documents, links, and specialist knowledge.'
    },
    {
      icon: Car,
      title: 'Field and service professionals',
      body: 'Upload manuals, procedures, and technical guides, then ask practical questions from the material while solving real work problems.'
    },
    {
      icon: Users,
      title: 'Professional learners',
      body: 'Study from your own documents, ask questions, review summaries, and build understanding with AI support.'
    }
  ];

  const useCases = [
    'A mechanic uploads a vehicle service manual and asks what to check when a radiator is leaking.',
    'A field engineer uploads equipment procedures and asks for the next safe troubleshooting step.',
    'A training company uploads course notes and turns them into a structured learning path.',
    'An organisation uploads onboarding and policy documents for employee learning and support.',
    'A professional learner uploads study materials and asks for explanations, summaries, and revision support.'
  ];

  const trustItems = [
    'Answers are grounded in the uploaded Source Set.',
    'Ask Source is designed to say when the uploaded material is not enough.',
    'Creators can review generated learning before publishing.',
    'Learners can continue through checks, progress, and mastery tracking.'
  ];

  return (
    <div className="flex flex-col min-h-screen bg-bg-main text-text-main selection:bg-indigo-500/30 font-sans">

      {/* 1. HERO */}
      <section className="relative pt-16 pb-16 lg:pt-14 lg:pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex flex-col lg:flex-row lg:items-stretch items-center gap-12 lg:gap-16 min-h-[calc(100vh-80px)] lg:min-h-0">
        <div className="flex-1 text-center lg:text-left z-10 w-full flex flex-col items-center lg:items-start pt-4 lg:justify-center overflow-hidden">
          <div className="w-full max-w-sm sm:max-w-md lg:max-w-xl overflow-hidden bg-primary/5 border border-primary/20 rounded-full py-2 mb-6 shadow-sm flex items-center">
            <div className="relative whitespace-nowrap flex items-center gap-2 text-primary font-bold tracking-wide text-xs sm:text-sm px-4">
              <Sparkles className="w-4 h-4 flex-shrink-0" /> Source grounded learning and operational assistance
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 leading-[1.1] text-text-main">
            Turn documents, manuals, and procedures into <span className="text-primary">AI powered learning and answers.</span>
          </h1>

          <p className="text-lg sm:text-xl text-text-alt mb-6 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
            Lumina Learn helps organisations, trainers, professionals, and field teams transform trusted sources into structured learning, source grounded answers, and measurable understanding.
          </p>

          <div className="bg-bg-alt/50 border border-border-main rounded-xl p-4 sm:p-6 mb-8 w-full max-w-2xl text-left border-l-4 border-l-primary shadow-sm">
            <h3 className="font-bold text-text-main mb-3">What you can do with Lumina:</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm sm:text-base text-text-alt font-medium">
              <li>Create a Source Set from documents, manuals, procedures, and URLs</li>
              <li>Ask Source for answers grounded in your uploaded material</li>
              <li>Generate a Document Brief when you need a clear summary</li>
              <li>Create Learning when you need a structured course or training path</li>
              <li>Track learner progress, checks, and mastery</li>
            </ol>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md lg:max-w-none justify-center lg:justify-start mb-6">
            <button
              onClick={() => openAuth('register', { intent: 'self_learn_from_document' })}
              className="px-6 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-[15px] sm:text-base transition-all flex items-center justify-center gap-2 shadow-xl shadow-indigo-500/20 active:scale-95 whitespace-nowrap"
            >
              Start with a Source Set
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={scrollToWaitlist}
              disabled={isLoggingIn}
              className="px-6 py-4 bg-bg-alt hover:bg-bg-alt/80 text-text-main rounded-xl font-bold text-[15px] sm:text-base transition-all border border-border-main active:scale-95 flex items-center justify-center disabled:opacity-70 whitespace-nowrap"
            >
              Request Pilot Access
            </button>
          </div>

          <div className="flex justify-center lg:justify-start w-full mt-2 lg:mt-4">
            <p className="inline-block text-xs sm:text-sm text-amber-700 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 px-4 py-3 rounded-lg max-w-2xl text-center lg:text-left shadow-sm">
              <span className="font-bold">Beta Note:</span> Lumina Learn is currently in beta. We are improving the platform with early users, training teams, and organisations.
            </p>
          </div>

          <div className="flex flex-wrap justify-center lg:justify-start gap-x-6 gap-y-3 opacity-70 mt-4">
            <div className="flex items-center gap-2 text-sm text-text-alt">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Source Sets
            </div>
            <div className="flex items-center gap-2 text-sm text-text-alt">
              <MessageSquare className="w-4 h-4 text-indigo-500" /> Ask Source
            </div>
            <div className="flex items-center gap-2 text-sm text-text-alt">
              <BookOpen className="w-4 h-4 text-emerald-500" /> Create Learning
            </div>
          </div>
        </div>

        <div className="flex-1 w-full max-w-lg lg:max-w-none relative mt-8 lg:mt-0 flex flex-col">
          <div className="relative rounded-3xl overflow-hidden border border-border-main shadow-2xl bg-bg-main flex-1 min-h-[390px] lg:min-h-0">
            <img
              src="https://i.postimg.cc/YqCx58J3/hero.png"
              alt="Lumina Learn turns source materials into learning and grounded answers"
              className="absolute inset-0 w-full h-full object-cover rounded-3xl hover:scale-[1.02] transition-transform duration-700"
              referrerPolicy="no-referrer"
            />
          </div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4/5 h-4/5 bg-indigo-500/20 blur-[100px] -z-10 rounded-full pointer-events-none"></div>
        </div>
      </section>

      {/* 2. WHO IT IS FOR */}
      <section className="py-20 bg-bg-main border-y border-border-main/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="text-sm font-bold tracking-widest uppercase text-primary mb-4">Built for people who depend on trusted knowledge</div>
            <h2 className="text-3xl sm:text-5xl font-bold text-text-main mb-6 tracking-tight">
              One platform for learning, support, and source grounded answers.
            </h2>
            <p className="text-lg text-text-alt max-w-3xl mx-auto leading-relaxed">
              Whether your sources are training notes, operating procedures, technical manuals, policies, or study material, Lumina helps turn them into useful learning and practical assistance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {audienceCards.map((card) => {
              const Icon = card.icon;
              return (
                <div key={card.title} className="bg-bg-alt/50 border border-border-main p-7 rounded-2xl shadow-sm hover:border-primary/40 transition-colors">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-5">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-lg text-text-main mb-3">{card.title}</h3>
                  <p className="text-sm text-text-alt leading-relaxed">{card.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3. CORE OFFERINGS */}
      <section className="py-24 bg-bg-alt/30 border-b border-border-main/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="text-sm font-bold tracking-widest uppercase text-primary mb-4">Two simple ways to use Lumina</div>
            <h2 className="text-3xl sm:text-5xl font-bold text-text-main mb-6 tracking-tight">
              Ask your sources. Or turn them into learning.
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="bg-bg-main border border-border-main rounded-[2rem] p-8 md:p-10 shadow-sm">
              <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center mb-6">
                <MessageSquare className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-text-main mb-4">Ask Source</h3>
              <p className="text-text-alt leading-relaxed mb-6">
                Ask direct questions from uploaded manuals, policies, documents, guides, and URLs. Lumina answers from the Source Set and shows source references where available.
              </p>
              <ul className="space-y-3 text-sm text-text-alt">
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> Practical questions from real documents</li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> Step by step explanations and summaries</li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> Useful for manuals, procedures, policies, and study materials</li>
              </ul>
            </div>

            <div className="bg-bg-main border border-border-main rounded-[2rem] p-8 md:p-10 shadow-sm">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center mb-6">
                <BookOpen className="w-7 h-7" />
              </div>
              <h3 className="text-2xl font-bold text-text-main mb-4">Create Learning</h3>
              <p className="text-text-alt leading-relaxed mb-6">
                Turn trusted materials into reviewable lessons, knowledge checks, and structured learning paths that trainers and organisations can refine before publishing.
              </p>
              <ul className="space-y-3 text-sm text-text-alt">
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> Course drafts from existing source material</li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> Review before publish workflow</li>
                <li className="flex gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> Knowledge checks, progress, and mastery support</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 4. FIELD AND SERVICE PROFESSIONALS */}
      <section className="py-24 bg-bg-main border-b border-border-main/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-sm font-bold tracking-widest uppercase text-primary mb-4">For field teams, technicians, engineers, and mechanics</div>
            <h2 className="text-3xl sm:text-5xl font-bold text-text-main mb-6 tracking-tight">
              Use manuals and procedures as an AI source assistant.
            </h2>
            <p className="text-lg text-text-alt leading-relaxed mb-6">
              Lumina is not limited to course creation. A field or service professional can upload a manual, service guide, operating procedure, or troubleshooting document and ask what the source says about a specific issue.
            </p>
            <div className="bg-bg-alt/50 border border-border-main rounded-2xl p-6">
              <p className="text-sm font-bold text-text-main mb-3">Example questions:</p>
              <ul className="space-y-3 text-sm text-text-alt">
                <li>“Based on this manual, what should I check first if the radiator is leaking?”</li>
                <li>“What does this procedure say about fault code E23?”</li>
                <li>“Explain step 4 of this maintenance process in simpler language.”</li>
                <li>“Summarise the safety warnings before I begin this task.”</li>
              </ul>
            </div>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-[2rem] p-8 md:p-10 shadow-xl shadow-primary/5">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <Activity className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-primary font-bold uppercase tracking-widest">Operational source support</p>
                <h3 className="text-xl font-bold text-text-main">Simple workflow</h3>
              </div>
            </div>
            <ol className="space-y-5 text-text-alt">
              <li className="flex gap-4">
                <span className="w-8 h-8 rounded-full bg-bg-main border border-border-main flex items-center justify-center font-bold text-primary shrink-0">1</span>
                <span>Upload the approved manual, procedure, guide, or URL into a Source Set.</span>
              </li>
              <li className="flex gap-4">
                <span className="w-8 h-8 rounded-full bg-bg-main border border-border-main flex items-center justify-center font-bold text-primary shrink-0">2</span>
                <span>Ask a practical question in normal language.</span>
              </li>
              <li className="flex gap-4">
                <span className="w-8 h-8 rounded-full bg-bg-main border border-border-main flex items-center justify-center font-bold text-primary shrink-0">3</span>
                <span>Receive an answer grounded in the uploaded material, with references where available.</span>
              </li>
              <li className="flex gap-4">
                <span className="w-8 h-8 rounded-full bg-bg-main border border-border-main flex items-center justify-center font-bold text-primary shrink-0">4</span>
                <span>If the source does not support the answer, Lumina should say the material is insufficient.</span>
              </li>
            </ol>
          </div>
        </div>
      </section>


      {/* 5. CLASSIC LEARNING EXPERIENCE */}
      <section className="py-24 bg-bg-alt/30 border-b border-border-main/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="text-sm font-bold tracking-widest uppercase text-primary mb-4">The classic guided learning path is still here</div>
            <h2 className="text-3xl sm:text-5xl font-bold text-text-main mb-6 tracking-tight">
              Upload content, take a course, and learn with the AI Tutor.
            </h2>
            <p className="text-lg text-text-alt max-w-3xl mx-auto leading-relaxed">
              Source Sets and Ask Source are powerful for direct answers, but Lumina also supports the full guided course experience for learners who need structure, lessons, checks, and tutor support.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-bg-main border border-border-main rounded-[2rem] p-8 shadow-sm">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-6">
                <UploadCloud className="w-7 h-7" />
              </div>
              <div className="text-sm font-bold tracking-widest uppercase text-primary mb-3">Step 1</div>
              <h3 className="text-2xl font-bold text-text-main mb-4">Upload learning material</h3>
              <p className="text-text-alt text-sm leading-relaxed">
                Start with documents, course notes, training material, or other trusted content. Lumina uses the material as the foundation for learning.
              </p>
            </div>

            <div className="bg-bg-main border border-border-main rounded-[2rem] p-8 shadow-sm">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center mb-6">
                <BookOpen className="w-7 h-7" />
              </div>
              <div className="text-sm font-bold tracking-widest uppercase text-primary mb-3">Step 2</div>
              <h3 className="text-2xl font-bold text-text-main mb-4">Take a guided course</h3>
              <p className="text-text-alt text-sm leading-relaxed">
                Learners move through structured lessons, knowledge checks, progress tracking, and mastery support instead of reading files passively.
              </p>
            </div>

            <div className="bg-bg-main border border-border-main rounded-[2rem] p-8 shadow-sm">
              <div className="w-14 h-14 rounded-2xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center mb-6">
                <MessageSquare className="w-7 h-7" />
              </div>
              <div className="text-sm font-bold tracking-widest uppercase text-primary mb-3">Step 3</div>
              <h3 className="text-2xl font-bold text-text-main mb-4">Ask the AI Tutor</h3>
              <p className="text-text-alt text-sm leading-relaxed">
                When learners are stuck, they can ask the AI Tutor to explain concepts, simplify lessons, give examples, or help them understand the material.
              </p>
            </div>
          </div>

          <div className="mt-12 bg-primary/5 border border-primary/20 rounded-[2rem] p-6 md:p-8 max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <div>
                <h3 className="text-xl font-bold text-text-main mb-3">Two paths, one knowledge platform.</h3>
                <p className="text-text-alt text-sm leading-relaxed">
                  Use Ask Source when you need immediate answers from uploaded material. Use Create Learning when you need a structured course, tutor support, knowledge checks, and learner progress.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="bg-bg-main border border-border-main rounded-xl p-4">
                  <p className="font-bold text-text-main mb-1">Ask Source</p>
                  <p className="text-xs text-text-alt">Best for direct questions, procedures, manuals, and quick explanations.</p>
                </div>
                <div className="bg-bg-main border border-border-main rounded-xl p-4">
                  <p className="font-bold text-text-main mb-1">Create Learning</p>
                  <p className="text-xs text-text-alt">Best for guided courses, lessons, quizzes, tutor support, and mastery tracking.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. HOW LUMINA WORKS */}
      <section className="py-24 bg-bg-alt/30 border-b border-border-main/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="text-sm font-bold tracking-widest uppercase text-primary mb-4">Upload. Ask. Create. Measure.</div>
            <h2 className="text-3xl sm:text-5xl font-bold text-text-main mb-4">
              Start with sources, then choose the outcome.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-center max-w-5xl mx-auto">
            <div className="p-8">
              <div className="w-16 h-16 rounded-2xl bg-bg-main border border-border-main flex items-center justify-center text-primary mx-auto mb-6 shadow-sm">
                <UploadCloud className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-text-main mb-3">Step 1: Build a Source Set</h3>
              <p className="text-text-alt text-sm leading-relaxed">
                Add readable PDFs, Word documents, manuals, procedures, and supported URLs into one trusted Source Set.
              </p>
            </div>

            <div className="p-8">
              <div className="w-16 h-16 rounded-2xl bg-bg-main border border-border-main flex items-center justify-center text-primary mx-auto mb-6 shadow-sm">
                <Sparkles className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-text-main mb-3">Step 2: Ask or create</h3>
              <p className="text-text-alt text-sm leading-relaxed">
                Ask questions directly from the Source Set, generate a summary, or create a reviewable learning draft.
              </p>
            </div>

            <div className="p-8">
              <div className="w-16 h-16 rounded-2xl bg-bg-main border border-border-main flex items-center justify-center text-primary mx-auto mb-6 shadow-sm">
                <Compass className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-text-main mb-3">Step 3: Use the knowledge</h3>
              <p className="text-text-alt text-sm leading-relaxed">
                Learners, teams, and professionals can practise, ask follow up questions, and track progress on desktop or mobile.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. USE CASES */}
      <section className="py-24 bg-bg-main border-b border-border-main/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="text-sm font-bold tracking-widest uppercase text-primary mb-4">Many use cases. One Source Set model.</div>
            <h2 className="text-3xl sm:text-5xl font-bold text-text-main mb-6 tracking-tight">
              Lumina works wherever trusted knowledge needs to become useful.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {useCases.map((item) => (
              <div key={item} className="bg-bg-alt/50 border border-border-main rounded-2xl p-6 flex gap-4">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                <p className="text-text-alt text-sm leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. TRUST */}
      <section className="py-24 bg-bg-alt/30 border-b border-border-main/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-sm font-bold tracking-widest uppercase text-primary mb-4">Designed for trusted source use</div>
            <h2 className="text-3xl sm:text-5xl font-bold text-text-main mb-6 tracking-tight">
              Grounding matters when answers are used for work and learning.
            </h2>
            <p className="text-lg text-text-alt leading-relaxed">
              Lumina is built around uploaded materials and Source Sets. The aim is not generic AI conversation. The aim is to help users work with the documents, manuals, procedures, and training materials they trust.
            </p>
          </div>

          <div className="bg-bg-main border border-border-main rounded-[2rem] p-8 md:p-10 shadow-sm">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-6">
              <ShieldCheck className="w-7 h-7" />
            </div>
            <ul className="space-y-4">
              {trustItems.map((item) => (
                <li key={item} className="flex gap-3 text-sm text-text-alt">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 8. EXPLAINER VIDEO */}
      <section className="py-20 bg-bg-main border-b border-border-main/30 relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="text-sm font-bold tracking-widest uppercase text-primary mb-4">See Lumina in action</div>
          <h2 className="text-3xl font-bold text-text-main mb-4 tracking-tight">See how Lumina works</h2>
          <p className="text-lg text-text-alt mb-10 max-w-2xl mx-auto">
            Watch how Lumina turns documents and URLs into Source Sets, grounded answers, structured learning, knowledge checks, and learner progress.
          </p>

          <div className="relative w-full aspect-video bg-bg-alt border border-border-main rounded-3xl overflow-hidden shadow-2xl flex items-center justify-center group mb-10">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-cyan-500/10 mix-blend-overlay pointer-events-none z-0"></div>
            <iframe
              className="absolute inset-0 w-full h-full z-10 rounded-3xl"
              src="https://www.youtube.com/embed/Uly0ymeRDCU?rel=0"
              title="Lumina Learn Explainer Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => openAuth('login')}
              className="w-full sm:w-auto px-8 py-3 bg-bg-alt border border-border-main text-text-main font-bold rounded-xl hover:bg-bg-alt/80 transition-all focus:outline-none focus:ring-2 focus:ring-border-main focus:ring-offset-2 focus:ring-offset-bg-main"
            >
              Log In
            </button>
            <button
              onClick={scrollToWaitlist}
              className="w-full sm:w-auto px-8 py-3 bg-primary text-bg-main font-bold rounded-xl hover:bg-primary/90 transition-all focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-bg-main shadow-lg shadow-primary/20"
            >
              Request Pilot Access
            </button>
          </div>
        </div>
      </section>

      {/* 9. PILOT CTA */}
      <section ref={waitlistRef} className="py-24 bg-bg-alt/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 text-sm font-bold tracking-widest uppercase text-primary mb-4">
            <Headphones className="w-4 h-4" /> Early pilot programme
          </div>
          <h2 className="text-3xl sm:text-5xl font-bold text-text-main mb-6 tracking-tight">
            Bring Lumina into your training, documents, or field knowledge workflow.
          </h2>
          <p className="text-lg text-text-alt mb-10 max-w-2xl mx-auto leading-relaxed">
            We are working with early users and organisations who want to turn trusted sources into learning, source grounded answers, and measurable outcomes.
          </p>

          <div className="bg-bg-main border border-border-main rounded-[2rem] p-6 md:p-8 shadow-sm text-left">
            <WaitlistForm />
          </div>

          <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 opacity-70 mt-8">
            <div className="flex items-center gap-2 text-sm text-text-alt">
              <Monitor className="w-4 h-4 text-indigo-500" /> Best on desktop for creating learning
            </div>
            <div className="flex items-center gap-2 text-sm text-text-alt">
              <Smartphone className="w-4 h-4 text-emerald-500" /> Use learning and Ask Source on desktop or mobile
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
